import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import useApiQuery from '../../hooks/useApiQuery'
import { fetchAutomationsDashboard } from '../../lib/api'
import { PageHeader, StatCard, Card, DataTable, StatusBadge } from '../../components/ui'

export default function AutomationsDashboard() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { data, loading } = useApiQuery(() => fetchAutomationsDashboard())

  const stats   = data?.stats || {}
  const docs    = data?.recent_documents   || []
  const plans   = data?.recent_test_plans  || []

  return (
    <div>
      <PageHeader
        title={`Good ${new Date().getHours() < 12 ? 'morning' : 'afternoon'}, ${user?.name?.split(' ')[0]} 👋`}
        subtitle="AI-powered documentation and QA automation"
      >
        <button className="btn-primary" onClick={() => navigate('/automations/projects')}>+ New Project</button>
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <StatCard label="Projects"        value={loading ? '—' : stats.projects       ?? 0} icon="⬡" color="#00C8E8" loading={loading} />
        <StatCard label="Repositories"    value={loading ? '—' : stats.repositories   ?? 0} icon="⌁" color="#1A6AF5" loading={loading} />
        <StatCard label="Documents"       value={loading ? '—' : stats.documents      ?? 0} icon="📄" color="#00E5C8" loading={loading} />
        <StatCard label="Test Plans"      value={loading ? '—' : stats.test_plans     ?? 0} icon="✓" color="#10B981" loading={loading} />
        <StatCard label="Specifications"  value={loading ? '—' : stats.specifications ?? 0} icon="◈" color="#A855F7" loading={loading} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-bold text-sm text-white">Recent Documents</h3>
            <button className="text-xs text-[var(--color-accent)] hover:underline" onClick={() => navigate('/automations/documents')}>View all →</button>
          </div>
          <DataTable
            loading={loading} data={docs} emptyText="No documents yet" emptyIcon="📄"
            columns={[
              { key: 'title',  label: 'Title',  render: r => <span className="text-xs text-white truncate max-w-[180px] block">{r.title}</span> },
              { key: 'status', label: 'Status', render: r => <StatusBadge status={r.status} /> },
              { key: 'updated_at', label: 'Updated', render: r => <span className="text-[11px] font-mono text-[var(--text-dim)]">{r.updated_at ? new Date(r.updated_at).toLocaleDateString() : '—'}</span> },
            ]}
            onRowClick={r => navigate(`/automations/documents/${r.id}`)}
          />
        </Card>

        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-bold text-sm text-white">Recent Test Plans</h3>
            <button className="text-xs text-[var(--color-accent)] hover:underline" onClick={() => navigate('/automations/test-plans')}>View all →</button>
          </div>
          <DataTable
            loading={loading} data={plans} emptyText="No test plans yet" emptyIcon="🧪"
            columns={[
              { key: 'title',  label: 'Title',  render: r => <span className="text-xs text-white truncate max-w-[180px] block">{r.title}</span> },
              { key: 'status', label: 'Status', render: r => <StatusBadge status={r.status} /> },
              { key: 'updated_at', label: 'Updated', render: r => <span className="text-[11px] font-mono text-[var(--text-dim)]">{r.updated_at ? new Date(r.updated_at).toLocaleDateString() : '—'}</span> },
            ]}
            onRowClick={r => navigate(`/automations/test-plans/${r.id}`)}
          />
        </Card>
      </div>

      <Card className="mt-6">
        <h3 className="font-display font-bold text-sm text-white mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[
            { icon: '⬡', label: 'New Project',        sub: 'Create a project workspace',  to: '/automations/projects' },
            { icon: '📄', label: 'Generate Document',  sub: 'AI-powered documentation',    to: '/automations/documents' },
            { icon: '🧪', label: 'Create Test Plan',   sub: 'Auto-generate QA test cases', to: '/automations/test-plans' },
          ].map(a => (
            <button key={a.label} onClick={() => navigate(a.to)}
              className="card-hover flex items-center gap-4 p-4 text-left group">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl flex-shrink-0"
                style={{ background: 'rgba(0,200,232,0.1)', border: '1px solid rgba(0,200,232,0.2)' }}>
                {a.icon}
              </div>
              <div>
                <p className="text-sm font-semibold text-white group-hover:text-[#00C8E8] transition-colors">{a.label}</p>
                <p className="text-[11px] text-[var(--text-dim)] mt-0.5">{a.sub}</p>
              </div>
            </button>
          ))}
        </div>
      </Card>
    </div>
  )
}
